import { useState, useEffect, useCallback } from "react";
import axios from '../../../api/apiClient';
import {
  CreateTableNew as CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage
} from '../../../common/APICommon';
import { SetAuditTrail } from '../../Reports/AuditTrailReportEmployeeSetup';
import { token, domain } from '../../Security';
import { LoadingSpinner } from '../../../common/Loading';
import { EmployeeHierarchyForm } from './forms/EmployeeHierarchyForm';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

function EmployeeHierarchy({ user, isHideSideMenu }) {
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  const headers = ["ID", "Team Name", "Employee ID", "Employee Name", "No. of Subordinates", "Is Active?", "Actions"];
  const accessors = ["id", "teamName", "employeeId", "employeeName", "subordinateCount", "isActive", "actions"];

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [data, setData] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  const [form, setForm] = useState({
    employeePersonalInformationId: "",
    employeeData: null,
    teamName: "",
    isActive: true,
    employees: [] 
  });

  const apiUrl = domain + "/api/EmployeeHierarchy";

  const resetLocalVariables = () => {
    setForm({
      employeePersonalInformationId: "",
      employeeData: null,
      teamName: "",
      isActive: true,
      employees: []
    });
    setSelectedData(null);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setData(response.data);
      setFilterData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilter = (value) => {
    setCurrentPage(1);
    const lower = value.toLowerCase();
    if (!value) { setData(filterData); return; }
    setData(filterData.filter(f => 
      f.employeeName?.toLowerCase().includes(lower) || 
      f.teamName?.toLowerCase().includes(lower) ||
      f.employeeId?.toString().includes(lower)
    ));
  };

  const handleSubmit = async () => {
    const apiData = {
      employeePersonalInformationId: form.employeePersonalInformationId,
      teamName: form.teamName,
      isActive: form.isActive,
      subordinates: form.employees.map(e => ({ employeePersonalInformationId: e.employeePersonalInformationId }))
    };

    try {
      setLoadingRequest(true);
      await axios.post(apiUrl, apiData, { headers: { 'Authorization': `Bearer ${token}` } });
      await SetAuditTrail({
        setupArea: "EmployeeHierarchy", actionTaken: "insert", field: "Add",
        newValue: `${form.teamName || form.employeeData.fullName}`, oldValue: "-",
        dateSaved: new Date(), savedById: user.id
      });
      await fetchData();
      showGeneralMessage("Hierarchy created successfully!", true);
      closeAddHandleClick();
    } catch (error) {
      showGeneralMessage("Failed to create hierarchy", false);
    } finally { setLoadingRequest(false); }
  };

  const handleUpdate = async () => {
    const apiData = {
      employeePersonalInformationId: form.employeePersonalInformationId,
      teamName: form.teamName,
      isActive: form.isActive,
      subordinates: form.employees.map(e => ({ employeePersonalInformationId: e.employeePersonalInformationId }))
    };

    try {
      setLoadingRequest(true);
      await axios.put(`${apiUrl}/${selectedData.id}`, apiData, { headers: { 'Authorization': `Bearer ${token}` } });
      await fetchData();
      showGeneralMessage("Hierarchy updated successfully!", true);
      closeEditHandleClick();
    } catch (error) {
      showGeneralMessage("Failed to update hierarchy", false);
    } finally { setLoadingRequest(false); }
  };

  const editHandleClick = (rowData) => {
    setSelectedData(rowData);
    setForm({
      employeePersonalInformationId: rowData.employeePersonalInformationId,
      teamName: rowData.teamName || "",
      employeeData: {
        id: rowData.employeePersonalInformationId,
        fullName: rowData.employeeName,
        employeeId: rowData.employeeId,
        department: rowData.department,
        position: rowData.position
      },
      isActive: rowData.isActive,
      employees: rowData.subordinates || [] 
    });
    setShowEditForm(true);
  };

  const openAddHandleClick = () => setShowAddForm(true);
  const closeAddHandleClick = () => { setShowAddForm(false); resetLocalVariables(); };
  const closeEditHandleClick = () => { setShowEditForm(false); resetLocalVariables(); };

  const customCellRenderer = (row, accessor) => {
    if (accessor === "isActive") {
      return row.isActive ? <span className="text-green-500 font-semibold">Active</span> : <span className="text-red-500 font-semibold">Inactive</span>;
    }
    if (accessor === "subordinateCount") {
      return <div className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-center text-xs w-20 font-bold border border-blue-200">{row[accessor]}</div>;
    }
    return <span className="text-gray-700 px-2">{row[accessor] || "-"}</span>;
  };

  const actions = [{ name: "Edit", icon: <EditIcon fontSize="small" />, className: "bg-green-500", function: editHandleClick, visible: true }];

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="MainteInfo shadow-xl">
            <div className="mainteHeader">
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Organization >"} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"Employee Hierarchy"}</span>
              </div>
              <h3>Employee Hierarchy</h3>
            </div>
            <div className='space-y-4 p-4'>
              <SearchBar handleFilter={handleFilter} />
              <Button icon={<AddIcon />} name={"Add Hierarchy"} className={"bg-customGreen"} onClick={openAddHandleClick} />
              <CreateTable tableName={"EmployeeHierarchy"} headers={headers} accessors={accessors} data={data} actions={actions} currentPage={currentPage} setCurrentPage={setCurrentPage} itemsPerPage={10} totalItems={data?.length} customCellRenderer={customCellRenderer} />
            </div>
          </div>
        </div>
      </div>
      <EmployeeHierarchyForm isOpen={showAddForm} onClose={closeAddHandleClick} form={form} setForm={setForm} handleSubmit={handleSubmit} modalName={"Add Employee Hierarchy"} mode="add" showGeneralMessage={showGeneralMessage} />
      <EmployeeHierarchyForm isOpen={showEditForm} onClose={closeEditHandleClick} form={form} setForm={setForm} handleSubmit={handleUpdate} modalName={"Update Employee Hierarchy"} mode="edit" showGeneralMessage={showGeneralMessage} />
      <GeneralMessage form={showMessage} message={message} isSuccess={isSuccess} />
      <LoadingSpinner loading={loading || loadingRequest} />
    </div>
  );
}

export default EmployeeHierarchy;